import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/cn.js";

/**
 * StatusPill — opinionated, semantic status pill for tabular contexts
 * (Iron Rule #16.3). Pre-defines the canonical EduViaOS statuses so
 * every page renders them identically.
 *
 * Why not reuse <Badge>? Because <Badge> is open-ended (variant=
 * default | secondary | success | warn | …) and a status column
 * needs the SAME mapping everywhere. Centralising the mapping here
 * means a tenant marked "active" looks identical on the Tenants
 * list, the Tenant detail page, and the audit log.
 *
 * Visual contract per the user-research refs (Shadcnblocks User List,
 * Sales Dashboard, CRM Dashboard):
 *   - Filled background at 10% saturation (`bg-{hue}-500/10`)
 *   - Stronger text in light mode (`text-{hue}-700`)
 *   - Lifted text in dark mode (`text-{hue}-400`)
 *   - No border (border lives in the background tint)
 *   - Rounded-full pill shape, NOT rectangular
 */

const statusPillVariants = cva(
  "inline-flex select-none items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium",
  {
    variants: {
      status: {
        active: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
        invited: "bg-amber-500/10 text-amber-700 dark:text-amber-300",
        suspended: "bg-rose-500/10 text-rose-700 dark:text-rose-300",
        inactive: "bg-muted text-muted-foreground",
        archived: "bg-muted text-muted-foreground decoration-muted-foreground/40 line-through",
        // Authoring lifecycle: a "draft" entity exists but isn't yet
        // published / activated. Distinct from `inactive` (turned off
        // forever) and from `invited` (waiting on a human action).
        // Slate hue is cool-but-neutral, signalling "work in progress".
        // Phase H10 — replaces the pre-H10 (mis-)mapping of
        // `template.status === "draft"` to the amber `invited` pill on
        // the assessment templates surfaces.
        draft: "bg-slate-500/10 text-slate-700 dark:text-slate-300",
        // Functional / process states.
        pending: "bg-sky-500/10 text-sky-700 dark:text-sky-300",
        success: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
        processing: "bg-sky-500/10 text-sky-700 dark:text-sky-300",
        failed: "bg-rose-500/10 text-rose-700 dark:text-rose-300",
        // Tenant-specific: the "no admin yet" warning state used on
        // the Tenants list when a tenant exists without an admin user
        // (deferred-invite flow). Same hue as `invited` but slightly
        // softer to differentiate.
        "no-admin": "bg-orange-500/10 text-orange-700 dark:text-orange-300",
      },
    },
    defaultVariants: { status: "active" },
  },
);

export type StatusPillStatus =
  | "active"
  | "invited"
  | "suspended"
  | "inactive"
  | "archived"
  | "draft"
  | "pending"
  | "success"
  | "processing"
  | "failed"
  | "no-admin";

export interface StatusPillProps
  extends React.HTMLAttributes<HTMLSpanElement>, VariantProps<typeof statusPillVariants> {
  /** Visible label. Defaults to a Title-cased version of `status`. */
  label?: React.ReactNode;
  /** Optional leading icon (a `<Lucide … />` component). */
  icon?: React.ReactNode;
}

const DEFAULT_LABELS: Record<StatusPillStatus, string> = {
  active: "Active",
  invited: "Invited",
  suspended: "Suspended",
  inactive: "Inactive",
  archived: "Archived",
  draft: "Draft",
  pending: "Pending",
  success: "Success",
  processing: "Processing",
  failed: "Failed",
  "no-admin": "No admin yet",
};

const StatusPill = React.forwardRef<HTMLSpanElement, StatusPillProps>(
  ({ className, status, label, icon, children, ...props }, ref) => {
    const resolvedStatus = (status ?? "active") as StatusPillStatus;
    const resolvedLabel = label ?? children ?? DEFAULT_LABELS[resolvedStatus];
    return (
      <span
        ref={ref}
        data-status={resolvedStatus}
        className={cn(statusPillVariants({ status }), className)}
        {...props}
      >
        {icon}
        {resolvedLabel}
      </span>
    );
  },
);
StatusPill.displayName = "StatusPill";

export { StatusPill, statusPillVariants };
